import { useMemo } from 'react';
import {
  computeStageBridge,
  metricFn,
  phaseBridgeCols,
  remainMirrorCols,
  summarySizeRows,
  fmt,
  fmtMoney,
  type MetricKey,
  type SeparationState,
  offeredMetrics,
} from '@integrate/core';
import { Waterfall } from './Waterfall.js';
import { MetricToggle } from './MetricToggle.js';
import { OneOffTable } from './OneOffTable.js';

/*
  The phase bridge: how the as-is organisation becomes the carve-out, one step at a time.

  Two waterfalls, both from core and both parity-checked column by column, colour included. The
  carve-out bridge subtracts `remainOrg + transfers` in its first step, which looks like a mistake and
  is not: a Day 1 transfer is still sitting in RemainCo when the perimeter is drawn, so it leaves here
  and comes back two columns later. The RemainCo mirror is the same population read from the other side.

  THE MIRROR IS SHOWN, NOT OFFERED. The app hides it behind a toggle that defaults off
  (orgchart.html:7812, `_pbMirror`), so the second half of the story — what the organisation paying for
  all of this actually looks like afterwards — was a click nobody knew to make. Both bridges are the
  same three cheap passes over the roster; there is no cost to showing them, and a page called "phase
  bridge" that draws one side of the phase is answering half the question.

  ONE SCENARIO, the one open. The app's version has a scenario picker (`_pbScenarioId`), which needs a
  sibling scenario's whole design and the client cannot fetch one yet — that is the transport work
  recorded against the perspective-comparison tab, and a picker that can only pick what is already on
  screen is a control that lies about what it does.
*/

export function PhaseBridgePane({
  state,
  scenarioName,
  metric,
  onMetric,
  onOpenAssumptions,
}: {
  state: SeparationState;
  scenarioName: string;
  /** Owned by the modal, so it survives a tab switch as the app's module-level `_pbMetric` did. */
  metric: MetricKey;
  onMetric(next: MetricKey): void;
  onOpenAssumptions(): void;
}): JSX.Element {

  // All three, always: the totals table needs them whatever the toggle says, and the toggle only
  // chooses which one the bars are drawn from.
  const bridges = useMemo(
    () => ({
      hc: computeStageBridge(state, { fn: metricFn('hc', state.assumptions) }),
      fte: computeStageBridge(state, { fn: metricFn('fte', state.assumptions) }),
      cost: computeStageBridge(state, { fn: metricFn('cost', state.assumptions) }),
    }),
    [state],
  );
  const B = bridges[metric];
  const main = useMemo(() => phaseBridgeCols(B), [B]);
  const mirror = useMemo(() => remainMirrorCols(B), [B]);

  /*
    The same six rows the summary prints, cut to the four carve-out stages the app's version of this
    table has (orgchart.html:7819-7826). One function, so the two tables cannot disagree about a stage
    they both name — and the two RemainCo rows are dropped here rather than reworded, because this is
    the carve-out's story and the mirror above already tells RemainCo's.
  */
  const rows = useMemo(
    () => summarySizeRows(bridges).filter((r) => !r.key.startsWith('remain-')),
    [bridges],
  );

  /*
    Asked of HEADCOUNT, never of the metric on screen — a roster imported with no salary column is a
    project with people in it, and "nobody counts in this design yet" would be a flatly wrong sentence
    about a real organisation.

    And asked of the WHOLE bridge, not only its as-is end. `computeStageBridge`'s build-up loop is
    independent of its employee loop, so a design that is nothing but planned hires — a roster of open
    positions, or one whose people all left at a refresh — has asis = 0 and a real Day 1 and Day 2. The
    first version of this gate suppressed both waterfalls and printed "nothing to bridge" directly above
    a totals table reading Day 1 = 10 and a one-off table charging for ten hires.
  */
  const empty = bridges.hc.asis <= 0 && bridges.hc.day1 <= 0 && bridges.hc.day2 <= 0;

  return (
    <div data-testid="phase-bridge-pane">
      {/* RAW, and the button says so: the stranded tab's Cost is the loaded one. */}
      <MetricToggle
        value={metric}
        onChange={onMetric}
        keys={offeredMetrics(state, ['hc', 'fte', 'cost'])}
        weight="raw"
        testIdPrefix="bridge-metric"
      />

      {empty ? (
        <p className="muted" data-testid="phase-bridge-empty">
          Nobody counts in this design yet, so there is nothing to bridge. Open positions, inactive
          people and attrition leavers are excluded throughout, which is the same population every
          number on a card uses.
        </p>
      ) : (
        <>
          <h4>Carve-out: As-is → Perimeter → Day 1 → Day 2</h4>
          <p className="muted small">
            The perimeter is the dedicated workforce, by threshold or by hand; transfers and Day 1
            build-ups bring the organisation to legal separation; rightsizing and TSA replacements take
            it to stand-alone. A future transfer counts as RemainCo until Day 1, which is why the first
            step takes it out and the fifth puts it back.
          </p>
          <Waterfall
            columns={main}
            metric={metric}
            testId="bridge-carve"
            caption="The carve-out bridge"
          />

          <h4>RemainCo mirror</h4>
          <p className="muted small">
            The same people from the other side: what stays once the perimeter and its Day 1 transfers
            have gone, then rightsized to steady state. Redeployments are absent by design — they keep
            people employed, so they move cost around inside RemainCo without changing its size.
          </p>
          <Waterfall
            columns={mirror}
            metric={metric}
            testId="bridge-remain"
            caption="The RemainCo mirror"
          />
        </>
      )}

      <h4>Stage totals</h4>
      <table className="data-table" data-testid="bridge-totals">
        <thead>
          <tr>
            <th>Stage of the carve-out</th>
            <th className="num">Headcount</th>
            <th className="num">FTE</th>
            <th className="num">Cost (raw)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.key}
              data-testid={`bridge-total-${r.key}`}
              data-stage={r.key}
              data-hc={r.hc}
              data-fte={r.fte}
              data-cost={r.cost}
              // As-is is the starting point rather than a stage of the design, and reads as context.
              className={r.key === 'asis' ? 'muted' : undefined}
              style={r.strong ? { fontWeight: 700 } : undefined}
            >
              <td>{r.label}</td>
              <td className="num">{fmt(r.hc)}</td>
              <td className="num">{fmt(r.fte, 1)}</td>
              <td className="num">{fmtMoney(r.cost)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4>One-off separation costs (severance + hiring)</h4>
      {/* The same table the summary prints, from the same component — see OneOffTable. */}
      <OneOffTable state={state} testId="bridge-oneoff" />

      <p className="muted small">
        <strong>{scenarioName}</strong>. Open positions, inactive people and attrition leavers are
        excluded throughout, matching the on-card numbers. Cost is raw annual salary; severance is
        months × monthly base salary per the country and seniority tiers; hiring is{' '}
        {fmt(state.assumptions.hiringCostPct)}% of a build-up&apos;s annual salary —{' '}
        <button
          type="button"
          className="linkish"
          data-testid="bridge-asm"
          onClick={onOpenAssumptions}
        >
          edit assumptions
        </button>
        .
      </p>
    </div>
  );
}
